import React, { useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  CircularProgress,
  Tooltip,
  Zoom,
  Alert,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningIcon from "@mui/icons-material/Warning";
import axiosInstance from "../../../services/axiosInstance";
import CallLogsTable from "./CallLogsTable";
import apiEndPointsConfig from "../../../services/apiEndPoints";

const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// ✅ NEW: Component to render SLA status
const SLABadge = ({ sla }) => {
  if (!sla) return <Typography fontSize={12}>-</Typography>;

  const isOnTime = sla.status === "On Time";
  const backgroundColor = isOnTime ? "#dcfce7" : "#fee2e2";
  const textColor = isOnTime ? "#16a34a" : "#dc2626";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "22px",
        backgroundColor: backgroundColor,
        color: textColor,
        fontWeight: 600,
        fontSize: "12px",
        fontFamily: "'Inter', sans-serif",
        p: 0.8,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: "inherit",
          fontSize: "inherit",
          lineHeight: 1,
        }}
      >
        {sla.status}
      </Typography>
    </Box>
  );
};

// ✅ NEW: Component to show duplicate badge
const DuplicateBadge = ({ isDuplicate, onViewDuplicates, phoneNumber }) => {
  if (!isDuplicate) return null;

  return (
    <Tooltip title="">
      <Chip
        label="Duplicate"
        size="small"
        color="warning"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          onViewDuplicates(phoneNumber);
        }}
        sx={{
          fontWeight: 600,
          fontFamily: "'Inter', sans-serif",
          fontSize: "11px",
          height: 20,
          cursor: "pointer",
          borderColor: "#f59e0b",
          color: "#d97706",
          "&:hover": {
            // backgroundColor: "#fef3c7",
            borderColor: "#d97706",
          },
        }}
      />
    </Tooltip>
  );
};

function Row({ row, isReachedLeads, getLeadsData, onViewDuplicates }) {
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const callLogs = Array.isArray(row?.callLogs) ? row.callLogs : [];
  const isDuplicate = row?.isDuplicate || false;

  const handleDelete = async () => {
    setError("");
    setDeleting(true);
    try {
      await axiosInstance.delete(
        `${apiEndPointsConfig.deleteCampaignLeadById}/${row?._id}`,
      );
      await getLeadsData();
      setDeleteDialogOpen(false);
    } catch (e) {
      setError(e?.message || "Failed to delete. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const isQualified = row?.isQualified;

  return (
    <>
      {/* ==== Main Row ==== */}
      <TableRow
        hover
        onClick={() => isReachedLeads && setOpen(!open)}
        sx={{
          "& > *": { borderBottom: "unset" },
          backgroundColor: open
            ? "#eef2ff"
            : isDuplicate
              ? "white"
              : "inherit",
          cursor: isReachedLeads ? "pointer" : "default",
          fontFamily: "'Inter', 'Poppins', 'Roboto', sans-serif",
          fontSize: "13px",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: isReachedLeads
              ? "#f8fafc"
              : isDuplicate
                ? "#fef9e7"
                : "#f9fafb",
          },
          borderLeft: isDuplicate ? "4px solid #f59e0b" : "none",
        }}
      >
        {/* Expand toggle for reached/qualified leads */}
        {isReachedLeads && (
          <TableCell sx={{ width: 45, py: 0.4 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(!open);
              }}
            >
              {open ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          </TableCell>
        )}

        {/* Phone */}
        <TableCell
          sx={{
            fontWeight: 500,
            color: "#1e293b",
            py: 0.8,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {row?.secNumber ?? row?.phoneNumber ?? "-"}
        </TableCell>

        {/* Name + Qualified + Duplicate Badges */}
        <TableCell sx={{ py: 0.8 }}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography
              noWrap
              fontWeight={600}
              sx={{
                fontSize: "13px",
                fontFamily: "'Poppins', 'Inter', sans-serif",
                color: "#111827",
                lineHeight: 1.2,
              }}
            >
              {row?.name || "-"}
            </Typography>
            {isDuplicate && (
              <DuplicateBadge
                isDuplicate={isDuplicate}
                onViewDuplicates={onViewDuplicates}
                phoneNumber={row?.phoneNumber}
              />
            )}
          </Box>
        </TableCell>

        {/* City */}
        <TableCell
          sx={{
            color: "#334155",
            py: 0.8,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {row?.city || "-"}
        </TableCell>

        {/* Email */}
        <TableCell>
          <Typography
            noWrap
            sx={{
              color: "#475569",
              fontSize: "13px",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {row?.email || "-"}
          </Typography>
        </TableCell>

        {/* Source */}
        <TableCell>
          <Typography
            noWrap
            sx={{
              color: "#475569",
              fontSize: "13px",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {row?.source || "-"}
          </Typography>
        </TableCell>

        {/* Title */}
        <TableCell>
          <Typography
            noWrap
            sx={{
              color: "#334155",
              fontSize: "13px",
              fontFamily: "'Poppins', 'Inter', sans-serif",
            }}
          >
            {row?.title || "-"}
          </Typography>
        </TableCell>

        {/* Location & Contact IDs */}
        <TableCell sx={{ color: "#475569", py: 0.8 }}>
          {row?.locationID || "-"}
        </TableCell>
        <TableCell sx={{ color: "#475569", py: 0.8 }}>
          {row?.contactID || "-"}
        </TableCell>

        {/* Created Time */}
        <TableCell
          sx={{
            color: "#475569",
            fontSize: "12.5px",
            py: 0.8,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {formatDateTime(row?.createdAt)}
        </TableCell>

        {/* SLA Column (only for reached/qualified leads) */}
        {isReachedLeads && (
          <TableCell sx={{ py: 0.8 }}>
            <SLABadge sla={row?.sla} />
          </TableCell>
        )}

        {/* Calls or Delete */}
        {isReachedLeads ? (
          <TableCell sx={{ py: 0.6 }}>
            <Chip
              label={`${row?.totalCalls || 0} calls`}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDialogOpen(true);
              }}
              sx={{
                backgroundColor: "#2563eb",
                color: "#fff",
                fontWeight: 500,
                fontFamily: "'Inter', sans-serif",
                fontSize: "11px",
                height: 22,
                "&:hover": { backgroundColor: "#1e40af" },
              }}
            />
          </TableCell>
        ) : (
          <TableCell sx={{ width: 50, py: 0.4 }}>
            <Tooltip title="Delete lead">
              <span>
                <IconButton
                  aria-label="delete"
                  color="error"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteDialogOpen(true);
                  }}
                  disabled={deleting}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </TableCell>
        )}
      </TableRow>

      {/* ==== Collapsible Call Logs ==== */}
      {isReachedLeads && (
        <TableRow>
          <TableCell colSpan={12} sx={{ p: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  m: 1.5,
                  borderRadius: 2,
                  p: 1.5,
                  backgroundColor: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  fontWeight={600}
                  sx={{
                    mb: 0.5,
                    fontSize: "13px",
                    color: "#1e293b",
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  Call History
                </Typography>
                {callLogs.length > 0 ? (
                  <CallLogsTable logs={callLogs} />
                ) : (
                  <Typography
                    fontSize={12}
                    color="text.secondary"
                    fontStyle="italic"
                  >
                    No call logs available.
                  </Typography>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}

      {/* ==== Delete Confirmation Dialog ==== */}
      {!isReachedLeads && (
        <Dialog
          open={deleteDialogOpen}
          onClose={() => (deleting ? null : setDeleteDialogOpen(false))}
          maxWidth="xs"
          fullWidth
          TransitionComponent={Zoom}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Delete Lead</DialogTitle>
          <Divider />
          <DialogContent sx={{ py: 2 }}>
            {isDuplicate && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This is a <strong>duplicate lead</strong>. Consider viewing
                other duplicates before deleting.
              </Alert>
            )}
            <Typography>
              Are you sure you want to delete this lead
              {row?.phoneNumber ? ` (${row.phoneNumber})` : ""}?
            </Typography>
            {error && (
              <Typography color="error" mt={1} fontSize={13}>
                {error}
              </Typography>
            )}
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              color="error"
              variant="contained"
              startIcon={deleting ? <CircularProgress size={16} /> : null}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

export default function NewLeadCollapsibleTable({
  data = [],
  getLeadsData,
  isReachedLeads = false,
  onViewDuplicates = () => { },
}) {
  // ✅ NEW: Calculate and display duplicate stats
  const duplicateCount = data.filter((d) => d.isDuplicate).length;
  const uniquePhoneCount = new Set(data.map((d) => d.phoneNumber)).size;

  if (!data || data.length === 0) {
    return (
      <Typography align="center" sx={{ py: 4, color: "text.secondary" }}>
        No leads found.
      </Typography>
    );
  }

  return (
    <>
      {/* ✅ NEW: Duplicate Alert */}

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
          maxHeight: "70vh",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "0px",
            height: "0px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f5f9",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "grey",
            borderRadius: "10px",
            transition: "background-color 0.2s ease",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "grey",
          },
          "& .MuiTableCell-root": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderBottom: "1px solid #f0f0f0",
            fontFamily: "'Inter', 'Poppins', 'Roboto', sans-serif",
          },
        }}
      >
        <Table stickyHeader aria-label="interactive table">
          <TableHead sx={{ backgroundColor: "#f8fafc" }}>
            <TableRow>
              {isReachedLeads && <TableCell sx={{ width: 50 }} />}
              <TableCell sx={{ fontWeight: 700 }}>Phone No</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>City</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Location ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Contact ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Lead Capture Time</TableCell>

              {isReachedLeads && (
                <TableCell sx={{ fontWeight: 700 }}>SLA Status</TableCell>
              )}

              <TableCell sx={{ fontWeight: 700 }}>
                {isReachedLeads ? "Calls" : "Actions"}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((row) => (
              <Row
                key={
                  row?._id ||
                  `${row?.phoneNumber}-${row?.createdAt}` ||
                  Math.random()
                }
                row={row}
                isReachedLeads={isReachedLeads}
                getLeadsData={getLeadsData}
                onViewDuplicates={onViewDuplicates}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
